/**
 * The range presets as chips (TMMAPP-0113 4e): Year to date · Last 12 months · This month · Last
 * month · Last year, then Custom — one vocabulary for the virtual account P&L and the envelope
 * page's window (TMMAPP-0114 A3). Each chip is a toggle button (`aria-pressed`) in a labelled group.
 */
import { useId } from 'react';
import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { resolveTokens } from '@/theme/theme';
import { RANGE_PRESETS, type RangePreset } from './downloadRange';

interface RangeChipsProps {
  /** The preset in force; `null` for a custom range. */
  preset: RangePreset | null;
  custom: boolean;
  onPreset: (preset: RangePreset) => void;
  onCustom: () => void;
  /** Replaces a preset's catalog label (a register search's "All time" on Year to date). */
  labels?: Partial<Record<RangePreset, string>> | undefined;
}

export function RangeChips({ preset, custom, onPreset, onCustom, labels }: RangeChipsProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const labelId = useId();
  const chip = (on: boolean) =>
    ({
      borderRadius: '16px',
      p: '5px 12px',
      fontSize: 13,
      border: `1px solid ${on ? tokens.brandBoundary : tokens.line}`,
      color: on ? tokens.link : tokens.text2,
      bgcolor: tokens.surfaceRaised,
      backgroundImage: on ? `linear-gradient(${tokens.selected}, ${tokens.selected})` : 'none',
      '&:hover': { borderColor: tokens.brandBoundary },
      '&.Mui-focusVisible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
    }) as const;
  return (
    <Box
      role="group"
      aria-labelledby={labelId}
      sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}
    >
      <Typography id={labelId} component="span" sx={visuallyHidden}>
        {t('pnl.rangeLabel')}
      </Typography>
      {RANGE_PRESETS.map((key) => (
        <ButtonBase
          key={key}
          aria-pressed={!custom && preset === key}
          onClick={() => onPreset(key)}
          sx={chip(!custom && preset === key)}
        >
          {labels?.[key] ?? t(`virtualAccount.download.preset.${key}`)}
        </ButtonBase>
      ))}
      <ButtonBase aria-pressed={custom} onClick={onCustom} sx={chip(custom)}>
        {t('pnl.rangeCustom')}
      </ButtonBase>
    </Box>
  );
}
